'use client';

import { ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Autocomplete from '@/app/_components/autocomplete';
import { ELButton, ELTextField } from '@davincihealthcare/elty-design-system-react';
import { ReactNode, useCallback, useMemo, useRef, useState, Fragment, RefObject, Suspense } from 'react';
import { handleServiceSelect, useSearchState } from '@/app/_components/widgets/profile/listing';
import { AggregatorWorkgroupInclinic } from '@/app/_components/widgets/profile/types';
import { AutocompleteItemOption } from '@/app/_components/autocomplete/types';
import { AUTOCOMPLETE } from '@/costants/enum';
import Modal from '@/app/_components/modal';
import Container from '@/app/_components/container';

import { useHeaderHeight } from '@/app/_hooks/useHeaderHeight';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import { capitalizeFirstCharInString } from '@/utils/string';

interface ProfileSearchProps {
  specializations: NonNullable<AggregatorWorkgroupInclinic['specializations']>;
  placeholder: string;
  searchModalVisible: boolean;
  setSearchModalVisible: (value: boolean) => void;
}

const SearchContent: React.FC<{
  specializations: NonNullable<AggregatorWorkgroupInclinic['specializations']>;
  handleSearch: (value: string, searchParams: URLSearchParams | null) => void;
  placeholder: string;
  showElButton?: boolean;
  onClick: () => void;
}> = ({ specializations, handleSearch, placeholder, showElButton, onClick }) => {
  const [shouldOpenMenu, setShouldOpenMenu] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const { inputValue, setInputValue, searchParams } = useSearchState();

  const specializationLabels = useMemo(
    () => [...new Set(specializations.map(specialization => specialization.label.toLowerCase()))],
    [specializations],
  );

  const options = useMemo<AutocompleteItemOption[]>(() => {
    const filteredLabels = inputValue
      ? specializationLabels.filter(label => label.includes(inputValue.toLowerCase()))
      : specializationLabels;
    const completeOptions = filteredLabels.map(label => ({
      value: label,
      label: capitalizeFirstCharInString(label),
      type: AUTOCOMPLETE.SPECIALITY,
    }));

    return [{ value: '', label: 'Vedi tutte le prestazioni', type: AUTOCOMPLETE.SPECIALITY }, ...completeOptions];
  }, [inputValue, specializationLabels]);

  return (
    <div className="flex-2 flex items-center gap-2">
      <Autocomplete
        shouldOpenMenu={shouldOpenMenu}
        onOptionClick={option => handleSearch(option.value, searchParams)}
        setShouldOpenMenu={setShouldOpenMenu}
        options={options}
        setInputValueRef={setInputValue}
        inputValueRef={inputValue}
        variant={'header'}
        id={`autocomplete-centri-medici`}
      >
        <ELTextField
          name="service"
          innerRef={ref as RefObject<HTMLInputElement>}
          aria-label="service"
          onClick={onClick}
          placeholder={placeholder}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          leadingIcon={<MagnifyingGlassIcon />}
          data-cy="input-searchbar"
        />
      </Autocomplete>
      {showElButton && (
        <div className="hidden h-full items-stretch lg:flex">
          <ELButton
            aria-label="Cerca"
            leadingIcon={<MagnifyingGlassIcon />}
            variant="filled"
            color="secondary"
            label="Cerca"
            size="small"
            onClick={() => handleSearch(inputValue, searchParams)}
          >
            Cerca
          </ELButton>
        </div>
      )}
    </div>
  );
};

const ModalContainer = ({ children, onClose, isModalOpen }: { children: ReactNode; onClose: () => void; isModalOpen: boolean }) => {
  if (!isModalOpen) return <>{children}</>;
  return (
    <Modal isModalOpen={true} className={'m-0 h-full max-h-full w-full max-w-full pb-5'} onClose={onClose}>
      <Container>
        <div className="flex items-center justify-start px-4 py-4">
          <ELButton
            label="Chiudi ricerca"
            aria-label="Chiudi ricerca"
            leadingIcon={<ArrowLeftIcon />}
            onClick={onClose}
            variant="text"
            color="primary"
          />
        </div>
      </Container>
      {children}
    </Modal>
  );
};
export const ProfileSearch = ({ specializations, placeholder, searchModalVisible, setSearchModalVisible }: ProfileSearchProps) => {
  const headerHeight = useHeaderHeight();

  const handleSearch = useCallback(
    (value: string, searchParams: URLSearchParams | null) => {
      handleServiceSelect(value, searchParams);
      setSearchModalVisible(false);
      const container = document.getElementById('profile-listing-container')?.getBoundingClientRect();
      if (container) {
        window.scroll({ top: window.scrollY + container.top - headerHeight - 64, behavior: 'smooth' });
      }
    },
    [headerHeight, setSearchModalVisible],
  );

  const commonSearchContentProps = useMemo(
    () => ({
      specializations,
      handleSearch,
      placeholder,
    }),
    [specializations, handleSearch, placeholder],
  );

  const isDesktopAndSmaller = useIsDesktop();

  return (
    <ModalContainer isModalOpen={searchModalVisible} onClose={() => setSearchModalVisible(false)}>
      <div
        className={`bg-brandBlue-200 py-3 ${searchModalVisible ? '' : 'sticky z-[25] lg:top-20 '}`}
        style={{ top: searchModalVisible ? undefined : headerHeight }}
        id="autocomplete-centri-medici-section"
      >
        <Container>
          <div className={`gap-x-16 gap-y-4 lg:grid lg:grid-cols-[2fr_1fr]`}>
            <Suspense fallback={<div className="h-12 lg:h-10" />}>
              <SearchContent
                {...commonSearchContentProps}
                showElButton={!isDesktopAndSmaller}
                onClick={() => {
                  if (isDesktopAndSmaller && !searchModalVisible) {
                    setSearchModalVisible(true);
                  }
                }}
              />
            </Suspense>
          </div>
        </Container>
      </div>
    </ModalContainer>
  );
};
